import { Monitor } from '@sortlist-frontend/mlm';
import { isBrowser } from '@sortlist-frontend/utils';
import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import { api as proxyApi, PUBLIC_API_PROXY_URL } from '_core/api/api-nextjs-proxy';
import { api as apiPublicApi } from '_core/api/api-public-api';
import { acceptLanguageHeader } from '_core/api/api.utils';
import { Domain, SearchPage } from '_types/public-api';

const pagesQuery = `
  query searchPages($host: String!, $associatedTopics: [ID!], $locale: ID!) {
    searchPages(
      first: 1,
      filter: {
        host: $host,
        locale: $locale,
        topics: $associatedTopics
      }
    ) {
      nodes {
        url
        id
      }
    }
  }
`;

export type SearchPageArgs = {
  domain?: Domain;
  topics: string[];
  locale: string;
  baseURL: string;
  enabled: boolean;
};

export type TopicType = 'service' | 'location';

export type FilterParams = {
  host: string;
  associatedTopics: string[];
  locale: string;
};

export const searchPageCacheKey = (params: FilterParams) => {
  return `searchPage:${params.host}:${params.associatedTopics.join(',')}:${params.locale}`;
};

export const pagesRepo = {
  getPages: async (params: FilterParams, locale: string, baseURL: string): Promise<SearchPage> => {
    const api = isBrowser() ? proxyApi : apiPublicApi;
    const url = isBrowser() ? PUBLIC_API_PROXY_URL : '/graphql';
    const config: AxiosRequestConfig = { headers: { 'Accept-Language': acceptLanguageHeader(locale) } };

    if (isBrowser()) config.baseURL = baseURL;

    const returnObject = await api.post(url, { query: pagesQuery, variables: { ...params } }, config);

    const response = returnObject?.data;

    if (response?.errors != null) {
      Monitor.captureException(
        new Error(`Error for searchTopics: ${JSON.stringify(returnObject?.data?.errors ?? {})}`),
      );
    }

    const result = response?.data?.searchPages?.nodes?.[0];

    return {
      id: result?.id ?? null,
      url: result?.url ?? '',
    };
  },
};

export const useSearchPages = ({ domain, topics, locale, baseURL, enabled = true }: SearchPageArgs) => {
  return useQuery({
    queryKey: ['topics', domain?.host ?? 'nodomain', topics.join(','), locale],
    queryFn: () => {
      if (domain == null) {
        return Promise.resolve(null);
      }
      const localeId = domain.availableLocales.find((domainLocale) => domainLocale.code === locale)?.id;
      if (localeId == null) {
        return Promise.resolve(null);
      }

      const params: FilterParams = {
        host: domain.host,
        associatedTopics: [localeId, ...topics],
        locale: localeId,
      };

      return pagesRepo.getPages(params, locale, baseURL);
    },
    enabled: enabled && Boolean(domain != null),
  });
};
